import { Fragment } from 'react';
import {
  BottomSheetContainer,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  IconAdd,
  IconClose,
  IconHeartOutline,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import { ScrollView } from 'react-native';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';
import { isNotNil } from 'shared/src/utils';
import { useLogClickMakeNewFolder } from './useLogClickMakeNewFolder';
import { useLogViewChoiceFolder } from './useLogViewChoiceFolder';

const THUMBNAIL_SIZE = 44;
const ROW_HEIGHT = 73;
const VISIBLE_ROWS = 6;
const SCROLL_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS;

interface FolderPickerBottomSheetParam {
  folders: LikeFolder[];
  currentFolderSno: number;
}

type FolderPickerResult = {
  isCreateNew: boolean;
  folder?: LikeFolder;
};

export const useFolderPickerBottomSheet = createUseBottomSheetModal<
  FolderPickerResult,
  FolderPickerBottomSheetParam
>(({ confirm, cancel, param }) => {
  useLogViewChoiceFolder(true);
  const logClickMakeNewFolder = useLogClickMakeNewFolder();
  return (
    <BottomSheetContainer>
      <TitleRow title={'옮길 서랍 선택'} onPressClose={cancel} />
      <Divider />
      <ScrollView style={{ height: SCROLL_HEIGHT }}>
        <NewFolderRow
          onPress={() => {
            logClickMakeNewFolder();
            confirm({ isCreateNew: true });
          }}
        />
        <Divider />
        {param.folders
          .filter(folder => folder.sno !== param.currentFolderSno)
          .map(folder => (
            <Fragment key={folder.sno}>
              <FolderRow
                folder={folder}
                onPress={() => confirm({ isCreateNew: false, folder })}
              />
              <Divider />
            </Fragment>
          ))}
      </ScrollView>
    </BottomSheetContainer>
  );
});

interface TitleRowProps {
  title: string;
  onPressClose: () => void;
}

const TitleRow = ({ title, onPressClose }: TitleRowProps) => (
  <Flex height={56} justifyContent={'center'} alignItems={'center'}>
    <Typography type={'subtitle1'} color={'content_primary'}>
      {title}
    </Typography>
    <Flex
      position={'absolute'}
      left={16}
      top={0}
      bottom={0}
      justifyContent={'center'}
      onPress={onPressClose}
    >
      <IconClose size={24} />
    </Flex>
  </Flex>
);

interface NewFolderRowProps {
  onPress: () => void;
}

const NewFolderRow = ({ onPress }: NewFolderRowProps) => (
  <Flex.Row
    alignItems={'center'}
    padding={{ horizontal: 16, vertical: 14 }}
    gap={12}
    onPress={onPress}
  >
    <Flex.Center
      width={THUMBNAIL_SIZE}
      height={THUMBNAIL_SIZE}
      backgroundColor={'bg_secondary'}
      borderRadius={'small'}
    >
      <IconAdd width={20} height={20} fill={ColorValues.blue40} />
    </Flex.Center>
    <Typography type={'subtitle2'} color={'content_primary'}>
      새 서랍 만들기
    </Typography>
  </Flex.Row>
);

interface FolderRowProps {
  folder: LikeFolder;
  onPress: () => void;
}

const FolderRow = ({ folder, onPress }: FolderRowProps) => {
  const thumbnailUri = folder.cover_image ?? folder.goods_images[0];
  return (
    <Flex.Row
      alignItems={'center'}
      padding={{ horizontal: 16, vertical: 14 }}
      gap={12}
      onPress={onPress}
    >
      {isNotNil(thumbnailUri) ? (
        <RemoteImage
          uri={thumbnailUri}
          width={THUMBNAIL_SIZE}
          height={THUMBNAIL_SIZE}
          borderRadius={'small'}
        />
      ) : (
        <Flex.Center
          width={THUMBNAIL_SIZE}
          height={THUMBNAIL_SIZE}
          backgroundColor={'bg_secondary'}
          borderRadius={'small'}
        >
          <IconHeartOutline
            width={20}
            height={20}
            fill={ColorValues.gray40}
          />
        </Flex.Center>
      )}
      <Flex.Row flex={1} alignItems={'baseline'} gap={4}>
        <Flex flexShrink={1}>
          <Typography
            type={'subtitle2'}
            color={'content_primary'}
            numberOfLines={1}
            ellipsizeMode={'tail'}
          >
            {folder.title}
          </Typography>
        </Flex>
        <Typography type={'body2'} color={'content_tertiary'}>
          {`(${folder.count})`}
        </Typography>
      </Flex.Row>
    </Flex.Row>
  );
};

const Divider = () => <Flex height={1} backgroundColor={'bg_tertiary'} />;